/* Libraries */
import React, { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { css } from 'aphrodite';
import { Link } from 'react-router-dom';
import qs from 'qs';
import { getFormattedDateFromIsoString } from '~/src/utils/date';
import { isTransferredAndNewerThan7Days } from './utils';
import { Chip, Typography } from '@mui/material';

/* Components */
import Table from '~/src/containers/Table';
import Button from '~/src/components/Button';
import Triangle from '~/src/components/Triangle';
import Dropdown from '~/src/components/Dropdown';
import LoadMoreDisplayText from '~/src/components/LoadMoreDisplayText';
import TemplateStatusBadge, { BADGE_CLASSES } from './TemplateStatusBadge';
import { clioWorkTemplateLink } from '~/src/utils/clio';
import { LoadingSpinner } from '~/src/components/ClioDocumentPicker/LoadingSpinner';

import dropdownStyles from '~/src/components/Dropdown/styles';

/* Hooks */
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import useMst from '~/src/hooks/useMst';

/* Styles */
import { ReactComponent as ItemSelectedIcon } from '~/src/static/item-selected.svg';
import styles from './styles';
import { TOTAL_DOCUMENT_LIMIT } from '~/src/utils/constants';

const DraftDocSetButton = ({ numDocs, nextPath }) => (
  <Link to={nextPath}>
    <Button primary>{`Draft ${numDocs} documents`}</Button>
  </Link>
);

const TemplatesActions = ({ selectedTemplateIds }) => {
  const numTemplates = selectedTemplateIds.length;
  const queryParams = qs.stringify(
    { ids: selectedTemplateIds },
    { encode: false },
  );
  const path = `/templates/docset/new?${queryParams}`;

  return (
    <div>
      {numTemplates > 0 && (
        <DraftDocSetButton numDocs={numTemplates} nextPath={path} />
      )}
    </div>
  );
};

const RowAction = ({
  templateId,
  onPreview,
  onRename,
  onDelete,
  hideDraftButton,
  onPreviewTemplate,
  templateSource,
  isPracticeAreaStandardContentEnabled,
  clioWorkLink,
  locked,
}) => {
  if (onPreviewTemplate) {
    return (
      <span analyticsname="Preview Template">
        <Button onClick={onPreviewTemplate}>
          <span>Preview</span>
        </Button>
      </span>
    );
  }

  const hideModifyActions =
    // A template can't be renamed or deleted while it's being copied to Clio Work.
    locked ||
    (isPracticeAreaStandardContentEnabled &&
      templateSource === 'clio_transferred');

  const renderTrigger = ({ toggleDropdown }) => {
    const handleClick = (event) => {
      event.stopPropagation();
      toggleDropdown();
    };
    return (
      <Button
        analyticsname="Actions"
        data-testid="action-btn-test-id"
        onClick={handleClick}
        cssStyle={styles.rowActionButton}
      >
        Actions <Triangle primary />
      </Button>
    );
  };

  return (
    <Dropdown renderTrigger={renderTrigger}>
      {clioWorkLink && (
        <a
          href={clioWorkLink}
          target="_blank"
          rel="noopener noreferrer"
          className={css(dropdownStyles.dropdownActionItem)}
          analyticsname="Open in Clio Work"
        >
          Open in Clio Work ↗
        </a>
      )}
      {!hideDraftButton && (
        <Link
          to={`/templates/${templateId}/new`}
          className={css(dropdownStyles.dropdownActionItem)}
          analyticsname="Draft New Document"
        >
          Draft new document
        </Link>
      )}
      <a
        onClick={onPreview}
        className={css(dropdownStyles.dropdownActionItem)}
        analyticsname="Preview Template"
      >
        Preview template
      </a>
      {!hideModifyActions && (
        <a
          onClick={onRename}
          className={css(dropdownStyles.dropdownActionItem)}
          analyticsname="Rename"
        >
          Rename
        </a>
      )}
      {onDelete && !hideModifyActions && (
        <a
          onClick={onDelete}
          className={css(
            dropdownStyles.dropdownActionItem,
            dropdownStyles.dropdownActionItemRed,
          )}
          analyticsname="Delete"
        >
          Delete
        </a>
      )}
    </Dropdown>
  );
};

// Selects every copyable template, or clears the selection once some are selected.
const SelectAllCheckbox = ({ selectedCount, selectableCount, onToggle }) => {
  const ref = useRef(null);
  const some = selectedCount > 0 && selectedCount < selectableCount;
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = some;
    }
  }, [some]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label="Select all templates"
      disabled={selectableCount === 0}
      checked={selectableCount > 0 && selectedCount === selectableCount}
      onClick={(event) => event.stopPropagation()}
      onChange={onToggle}
    />
  );
};

const Templates = ({
  templates,
  visibleItems,
  empty,
  rowActions,
  onAddTemplateToSeletionSidebar,
  selectedTemplateIds,
  onSearchChange,
  onSortByChange,
  hideDraftButton,
  onPreviewTemplate,
  cardTitle = 'Firm Templates',
  loading,
  done,
  onLoadMore,
  banner,
  clioWorkCopy,
}) => {
  const { sidebarItemTotalNumber, containedInSidebar } = useMst((store) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useObserver(() => ({
      sidebarItemTotalNumber: store.sidebarItems.items.length,
      containedInSidebar: store.sidebarItems.containedInSidebar,
    })),
  );

  const {
    isUnifiedPlatform,
    isPracticeAreaStandardContentEnabled,
    isWordDocumentAutomationEnabled,
  } = useFeatureAccesses();

  /*
  ProX, by default, is used for word template automation.
  Subscription "word template automation" does NOT exist in ProX.
  */
  const allowDraftWithWordDocument =
    !isUnifiedPlatform ||
    (isUnifiedPlatform && isWordDocumentAutomationEnabled);

  const renderAddButtonOrAddedIcon = (template) => {
    if (containedInSidebar(template)) {
      return (
        <div className="flex">
          <div className="mr-2 flex items-center">
            <ItemSelectedIcon />
          </div>
          <div className="leading-none">Added</div>
        </div>
      );
    }

    return (
      <div>
        <Button
          data-tip
          data-for={`add-button-${template.id}`}
          onClick={(event) => {
            event.stopPropagation();
            onAddTemplateToSeletionSidebar(template);
          }}
          disabled={
            !allowDraftWithWordDocument ||
            sidebarItemTotalNumber >= TOTAL_DOCUMENT_LIMIT ||
            isCopying(template)
          }
        >
          <span>Add</span>

          <ReactTooltip
            id={`add-button-${template.id}`}
            place="bottom"
            effect="solid"
            getContent={() => {
              if (!allowDraftWithWordDocument) {
                return 'Word document automation subscription is required';
              }
              if (sidebarItemTotalNumber >= TOTAL_DOCUMENT_LIMIT) {
                return 'Cannot add more than 20 documents to selection.';
              }
              if (isCopying(template)) {
                return 'This template is being copied to Clio Work.';
              }
              return null;
            }}
          ></ReactTooltip>
        </Button>
      </div>
    );
  };

  const renderRowAction = (template) => {
    return (
      <div className="flex">
        <div className="mr-6 flex items-center">
          {renderAddButtonOrAddedIcon(template)}
        </div>

        {allowDraftWithWordDocument && (
          <RowAction
            templateId={template.id}
            onPreview={() => rowActions.preview(template)}
            onRename={() => rowActions.rename(template)}
            onDelete={() => rowActions.delete(template)}
            hideDraftButton={hideDraftButton}
            onPreviewTemplate={
              onPreviewTemplate ? () => onPreviewTemplate(template) : undefined
            }
            templateSource={template.templateSource}
            isPracticeAreaStandardContentEnabled={
              isPracticeAreaStandardContentEnabled
            }
            clioWorkLink={clioWorkLinkFor(template)}
            locked={isCopying(template)}
          />
        )}
      </div>
    );
  };

  const renderTransferredColumn = () => {
    return (
      <Chip
        label={
          <Typography
            variant={'smallLabel'}
            sx={(theme) => ({
              color: theme.palette.blue[700],
            })}
          >
            New
          </Typography>
        }
        sx={(theme) => ({
          background: theme.palette.blue[50],
        })}
      />
    );
  };

  const copyStateFor = (template) =>
    clioWorkCopy?.statesById.get(Number(template.id));

  const isCopying = (template) => copyStateFor(template)?.status === 'copying';

  // A template with a copy in Clio Work links to it, even when a later copy of it failed.
  const clioWorkLinkFor = (template) => {
    const copyState = copyStateFor(template);
    return copyState?.document_id
      ? clioWorkTemplateLink(copyState.document_id, clioWorkCopy.folderId)
      : undefined;
  };

  const renderOpenLink = (copyState) => (
    <a
      className="ml-2 text-xs text-blue-700"
      href={clioWorkTemplateLink(copyState.document_id, clioWorkCopy.folderId)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      Open ↗
    </a>
  );

  const renderCopyCheckbox = (template) => {
    const id = Number(template.id);
    if (!clioWorkCopy.selectableIds.has(id)) {
      const copyState = copyStateFor(template);
      if (copyState?.status !== 'in_clio_work' || copyState.out_of_sync) {
        return null;
      }
      // A disabled input fires no mouse events, so the tooltip hangs on its wrapper.
      return (
        <span data-tip data-for={`up-to-date-${id}`}>
          <input
            type="checkbox"
            aria-label={`Select ${template.title}`}
            disabled
            onClick={(event) => event.stopPropagation()}
          />
          <ReactTooltip id={`up-to-date-${id}`} place="bottom" effect="solid">
            Already up to date in Clio Work
          </ReactTooltip>
        </span>
      );
    }
    return (
      <input
        type="checkbox"
        aria-label={`Select ${template.title}`}
        checked={clioWorkCopy.selectedIds.has(id)}
        onClick={(event) => event.stopPropagation()}
        onChange={() => clioWorkCopy.onToggle(id)}
      />
    );
  };

  const renderTitle = (title, { rowData }) => {
    const copyState = copyStateFor(rowData);
    if (copyState?.status === 'not_copied' && copyState.suggested) {
      return (
        <span>
          {title}
          <span className={`ml-2 ${BADGE_CLASSES} bg-blue-50 text-blue-700`}>
            Suggested
          </span>
        </span>
      );
    }
    if (copyState?.status !== 'failed') {
      return title;
    }
    return (
      <div>
        <div>{title}</div>
        <div className="text-xs text-red-700">
          {copyState.can_retry ? (
            <>
              Something went wrong on our side.{' '}
              {/* One copy at a time: no retry while another copy runs. */}
              {!clioWorkCopy.copying && (
                <a
                  className="underline"
                  onClick={(event) => {
                    event.stopPropagation();
                    clioWorkCopy.onRetry([copyState.template_id]);
                  }}
                >
                  Retry
                </a>
              )}
            </>
          ) : (
            <>
              Trying again did not fix it.{' '}
              <a className="underline" href="mailto:support@clio.com">
                Contact our support team.
              </a>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCopyStatus = (copyState) => {
    if (copyState.status === 'copying') {
      return (
        <div className="flex flex-wrap items-center text-sm">
          <LoadingSpinner />
          <span className="ml-2">
            {copyState.progress_percent == null
              ? 'Copying…'
              : `Copying (${copyState.progress_percent}%)`}
          </span>
        </div>
      );
    }
    if (copyState.status === 'in_clio_work' && copyState.out_of_sync) {
      return (
        <div className="flex flex-wrap items-center text-sm">
          <span
            className={`${BADGE_CLASSES} bg-yellow-50 text-yellow-700`}
            data-tip
            data-for={`out-of-sync-${copyState.template_id}`}
          >
            Changes made
          </span>
          <ReactTooltip
            id={`out-of-sync-${copyState.template_id}`}
            place="bottom"
            effect="solid"
          >
            {
              'Changes have been made to this template since it was last migrated to Clio Work. Select "Update" to copy the changes across to the template in Clio Work.'
            }
          </ReactTooltip>
          {/* One copy at a time: no update while another copy runs. */}
          {!clioWorkCopy.copying && (
            <a
              className="ml-2 text-xs text-blue-700"
              onClick={(event) => {
                event.stopPropagation();
                clioWorkCopy.onRetry([copyState.template_id]);
              }}
            >
              Update
            </a>
          )}
        </div>
      );
    }
    if (copyState.status === 'in_clio_work') {
      return (
        <div className="flex flex-wrap items-center text-sm">
          <span className={`${BADGE_CLASSES} bg-purple-50 text-purple-700`}>
            In Clio Work
          </span>
          {renderOpenLink(copyState)}
        </div>
      );
    }
    // A failed copy of a template already in Clio Work still links to that copy.
    return (
      <div className="flex flex-wrap items-center text-sm">
        <span className={`${BADGE_CLASSES} bg-red-50 text-red-700`}>
          {"Couldn't copy"}
        </span>
        {copyState.document_id && renderOpenLink(copyState)}
      </div>
    );
  };

  // While templates are selected, a bar for them takes the column headers' place.
  const renderBulkBar = () => {
    const { selectedIds, selectableIds } = clioWorkCopy;
    return (
      <div className="flex items-center border-b bg-gray-50 px-4 py-2 text-sm">
        <SelectAllCheckbox
          selectedCount={selectedIds.size}
          selectableCount={selectableIds.size}
          onToggle={clioWorkCopy.onClear}
        />
        <span className="ml-3 font-semibold">
          {selectedIds.size} of {selectableIds.size} selected
        </span>
        {selectedIds.size < selectableIds.size && (
          <button
            type="button"
            className="ml-3 text-blue-700"
            onClick={clioWorkCopy.onSelectAll}
          >
            Select all {selectableIds.size}
          </button>
        )}
        <button
          type="button"
          className="ml-3 text-blue-700"
          onClick={clioWorkCopy.onClear}
        >
          Clear
        </button>
        <span className="ml-auto">
          {/* One copy at a time: the next one waits for the running copy to end. */}
          <Button
            primary
            small
            disabled={clioWorkCopy.copying}
            onClick={clioWorkCopy.onCopySelected}
          >
            Copy {selectedIds.size} to Clio Work
          </Button>
        </span>
      </div>
    );
  };

  const renderStatusWithTransferred = (status, { rowData }) => {
    const copyState = copyStateFor(rowData);
    if (copyState && copyState.status !== 'not_copied') {
      return renderCopyStatus(copyState);
    }
    const showTransferredPill = isTransferredAndNewerThan7Days(rowData);

    return showTransferredPill ? (
      renderTransferredColumn()
    ) : (
      <TemplateStatusBadge status={status} />
    );
  };

  return (
    <div analyticsname="Template Actions" className="flex">
      <div
        className="bg-white border border-gray-200 rounded-lg p-6 mx-auto"
        style={{ width: '100%', maxWidth: '1080px' }}
      >
        <h3 className="text-lg font-semibold mb-4">{cardTitle}</h3>
        {banner}

        <div className="pb-6 pt-6">
          <Table
            emptyTitle={'No templates match that filter'}
            empty={empty}
            isLoading={false}
            searchPlaceholder="Search templates"
            renderActions={() => (
              <TemplatesActions selectedTemplateIds={selectedTemplateIds} />
            )}
            data={templates}
            visibleItems={visibleItems}
            onSearchChange={onSearchChange}
            onSortByChange={onSortByChange}
            columnHeadersReplacement={
              clioWorkCopy?.selectedIds.size > 0 ? renderBulkBar() : undefined
            }
            columns={[
              ...(clioWorkCopy
                ? [
                    {
                      label: 'Select',
                      hideLabel: true,
                      disableSort: true,
                      renderHeader: () => (
                        <SelectAllCheckbox
                          selectedCount={clioWorkCopy.selectedIds.size}
                          selectableCount={clioWorkCopy.selectableIds.size}
                          onToggle={clioWorkCopy.onSelectAll}
                        />
                      ),
                      render: renderCopyCheckbox,
                      style: { width: '4%', textAlign: 'left' },
                    },
                  ]
                : []),
              {
                label: 'Template name',
                dataKey: 'title',
                render: clioWorkCopy ? renderTitle : undefined,
                style: {
                  width: '30%',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                },
              },
              {
                label: 'Status',
                dataKey: 'status',
                render: renderStatusWithTransferred,
                style: { width: '15%', textAlign: 'left' },
                disableSort: true, // Status is a computed field, not sortable in database
              },
              {
                label: 'Last updated',
                dataKey: 'updatedAt',
                render: getFormattedDateFromIsoString,
                style: { width: '10%', textAlign: 'left' },
              },
              {
                label: 'Actions',
                hideLabel: true,
                render: renderRowAction,
                style: {
                  flexGrow: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                },
              },
            ]}
          />
        </div>
        {clioWorkCopy?.preview ? (
          <div className="text-sm text-gray-600">
            Showing {clioWorkCopy.preview.shown} of {clioWorkCopy.preview.total}{' '}
            · suggested first ·{' '}
            <button
              type="button"
              className="text-blue-700"
              onClick={clioWorkCopy.preview.onShowAll}
            >
              Show all
            </button>
          </div>
        ) : (
          <LoadMoreDisplayText
            loading={loading}
            done={done}
            onLoadMore={onLoadMore}
          />
        )}
      </div>
    </div>
  );
};

Templates.propTypes = {
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      updatedAt: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
  rowActions: PropTypes.shape({
    draft: PropTypes.func,
    preview: PropTypes.func,
    rename: PropTypes.func,
    delete: PropTypes.func,
  }),

  onSearchChange: PropTypes.func,
  banner: PropTypes.node,
  onSortByChange: PropTypes.func,
  hideDraftButton: PropTypes.bool,
  onPreviewTemplate: PropTypes.func,
  loading: PropTypes.bool,
  done: PropTypes.bool,
  onLoadMore: PropTypes.func,
  // Copying templates to Clio Work; only the Library's firm templates pass it.
  clioWorkCopy: PropTypes.shape({
    statesById: PropTypes.instanceOf(Map).isRequired,
    selectableIds: PropTypes.instanceOf(Set).isRequired,
    selectedIds: PropTypes.instanceOf(Set).isRequired,
    folderId: PropTypes.number,
    onToggle: PropTypes.func.isRequired,
    onSelectAll: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onCopySelected: PropTypes.func.isRequired,
    // Copies the given templates (a retry, or an update of one with changes made).
    onRetry: PropTypes.func.isRequired,
    // Only the first rows are shown, suggested first, until the user asks for all of them.
    preview: PropTypes.shape({
      shown: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
      onShowAll: PropTypes.func.isRequired,
    }),
    // A copy is running, so nothing can be copied or retried until it ends.
    copying: PropTypes.bool,
  }),
};

Templates.defaultProps = {
  templates: [],
};

export default Templates;
